import { useInfiniteQuery, useQueryClient, useQuery } from 'react-query';

import { pageSizeTypes } from 'utils/pagination';
import { extractRow } from 'utils/inventories/responseTransformer';

import { RowProps } from 'types/reportsTablesTypes';

import { useQueryParams } from 'hooks';
import { reportsTypes } from 'utils/reports';
import { getUserCurrentStore } from 'utils/auth';

import { getCashRegisterSessions, getDailySettlement, getDailySettlementDetails } from './services';

const QUERY_KEY = 'reportCashRegisterSessions';
const QUERY_KEY_DAILY_SETTLEMENT = 'reportDailySettlement';
export const API_BASE_URL = `${process.env.REACT_APP_BASE_URL_API}/api`;

interface QueryParamsType {
  startDate: string;
  endDate: string;
  status?: string;
}

export function useCashRegisterSessions() {
  const { queryParams } = useQueryParams<QueryParamsType>();
  const startDate = queryParams?.startDate || '';
  const endDate = queryParams?.endDate || '';
  const status = queryParams?.status || '';
  const {
    refetch,
    data,
    isError,
    isFetched,
    isFetching,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    remove,
  } = useInfiniteQuery(
    [QUERY_KEY, `${startDate}_${endDate}_${status}`],
    ({ pageParam = 1 }) =>
      getCashRegisterSessions({
        page: pageParam,
        pageSize: pageSizeTypes.PAGE_SIZE_CASH_REGISTER,
        startDate,
        endDate,
        ...(status !== 'ALL' ? { state: status } : {}),
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage?.pagination?.next) {
          return undefined;
        }
        return pages.length + 1;
      },
    },
  );

  return {
    cashRegisterSessions: {
      headers: data?.pages[0]?.data.headers,
      rows: extractRow(data?.pages || []) as RowProps[],
    },
    cashRegisterSessionsState: {
      isError,
      isFetching,
      isFetchingNextPage,
      isFetched,
      isLoading,
    },
    fetchNextPage,
    hasNextPage,
    refetchCashRegisterSessions: refetch,
    removeCashRegisterSessionsQuery: remove,
  };
}

export function useDailySettlement() {
  const { queryParams } = useQueryParams<QueryParamsType>();
  const startDate = queryParams?.startDate || '';
  const endDate = queryParams?.endDate || '';
  const query = useInfiniteQuery(
    [QUERY_KEY_DAILY_SETTLEMENT, queryParams],
    ({ pageParam = 1 }) =>
      getDailySettlement({
        page: pageParam,
        pageSize: pageSizeTypes.PAGE_SIZE_CASH_REGISTER,
        startDate,
        endDate,
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage?.pagination?.next) return undefined;
        return pages.length + 1;
      },
      enabled: !!startDate && !!endDate,
    },
  );
  return {
    ...query,
    dailySettlement: {
      headers: query.data?.pages[0]?.data.headers,
      rows: extractRow(query.data?.pages || []) as RowProps[],
    },
  };
}

export const useReportsDailySettlementDetails = ({ uuidKey }: { uuidKey: string }) => {
  const storeUuid = getUserCurrentStore()?.uuid;

  const query = useQuery({
    queryKey: ['reportDailySettlementDetails', uuidKey],
    queryFn: () => getDailySettlementDetails(uuidKey),
    enabled: !!uuidKey && !!storeUuid,
  });

  return {
    reportSessionsDetails: query?.data?.data,
    ...query,
  };
};

export const useDailySettlementDetailsCashOuts = ({ cashSessionUuid }: { cashSessionUuid: string }) => {
  const query = useInfiniteQuery({
    queryKey: ['reportDailySettlementDetailsCashOuts', cashSessionUuid],
    queryFn: async ({ pageParam = 1 }) =>
      getCashRegisterSessions({ page: pageParam, pageSize: pageSizeTypes.PAGE_SIZE_CASH_REGISTER, cashSessionUuid }),
    getNextPageParam: (lastPage, pages) => {
      if (!lastPage?.pagination?.next) return undefined;
      return pages.length + 1;
    },
    enabled: !!cashSessionUuid,
  });

  const headers = query.data?.pages[0]?.data.headers ?? [];

  return {
    cashOuts: {
      headers,
      rows: extractRow(query.data?.pages || []),
    },
    ...query,
  };
};

export const useDailySettlementUrl = () => {
  const queryClient = useQueryClient();
  const cachedUrlParams = queryClient.getQueryData(['dailySettlement', 'urlParams']);
  const baseUrl = `/reports/${reportsTypes.CASH_REGISTER}/daily-settlement`;
  const url = cachedUrlParams ? `${baseUrl}?${cachedUrlParams}` : `${baseUrl}`;

  const setCachedParams = (params: QueryParamsType) => {
    queryClient.setQueryData(['dailySettlement', 'urlParams'], () => {
      const { startDate, endDate } = params;
      const urlParams = new URLSearchParams({
        startDate,
        endDate,
      }).toString();
      return urlParams;
    });
  };

  return {
    setCachedParams,
    urlParams: cachedUrlParams,
    baseUrl,
    url,
  };
};

export const useCashOutsUrl = () => {
  const queryClient = useQueryClient();
  const cachedUrlParams = queryClient.getQueryData(['cashOuts', 'urlParams']);
  const baseUrl = `/reports/${reportsTypes.CASH_REGISTER}/cash-outs`;
  const url = cachedUrlParams ? `${baseUrl}?${cachedUrlParams}` : `${baseUrl}`;

  const setCachedParams = (params: QueryParamsType) => {
    queryClient.setQueryData(['cashOuts', 'urlParams'], (oldParams: string | undefined) => {
      const searchParams = new URLSearchParams(oldParams);
      Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.set(key, value);
      });
      return searchParams.toString();
    });
  };

  return {
    setCachedParams,
    urlParams: cachedUrlParams,
    baseUrl,
    url,
  };
};
